/**
 * ================================================================================================
 * **F4-1 + F4-3 — HER ASK LALA FUNNEL'S DOOR, AND THE LANDING AT THE OTHER END OF IT.**
 *
 * HER F4-1: *"A funnel door named clean for ads, fast, ONE action, the LANDER-1 speed laws.
 * 'She already knows. Ask her anything.' energy."*
 * HER F4-3: *"SHE LANDS DIRECTLY IN AN ASK LALA CONVERSATION, already open, Lala speaking FIRST —
 * never the room tour."*
 *
 * This module imports NOTHING, which is the entry requirement that lets `scripts/f41-talk-door.mjs`
 * LOAD and RUN every law in it rather than read one out of a twelve-thousand-line component.
 * ================================================================================================
 * >>> IT IS NOT A FOURTH FUNNEL KEY, AND THAT REFUSAL IS THE FIRST DECISION OF THE ROUND. <<<
 *
 * FUNNEL-4's own build note names the door as *"`/funnel?f=lala` or `/talk`"* — and measured against
 * the code, the first spelling cannot be built. `FUNNEL_KEYS` is `["wealthy","psychic","timeline"]`
 * and a key on that roster is a door that CAPTURES A LEAD AND DELIVERS A PAID READING BY EMAIL
 * (F1/F3/L11). **Her clause 2 for this funnel is SIGNUP FIRST**, so a fourth key would buy a
 * generation her own flow does not want, land a stranger on a reading rather than on an account, and
 * put a free-reading door where she asked for a conversation. `/talk` is its own page.
 * ================================================================================================
 * >>> THE LANDING IS A STASH AND NOT CLIENT STATE, AND THE REASON IS MEASURED RATHER THAN STYLISTIC.
 * <<<
 *
 * `openSignup(intent, next)` already carries a destination — and it carries it in `pendingPage`,
 * which is **CONSUMED AT SIGN-IN** (`App.tsx`'s `isLoggedIn && page === "signup"` arm) long before
 * her room is born. So by the time `enterRoom()` runs, the destination is already spent: a build that
 * reaches for `pendingPage` here reads as correct in every diff and lands her in My Room anyway.
 *
 * **AND CLIENT STATE ALONE LOSES THE GOOGLE HALF OF HER AUDIENCE, SILENTLY.**
 * *"Continue with Google"* is `window.location.href = "/api/auth/google/start"` — a FULL PAGE
 * NAVIGATION — so React remounts and every value in memory is gone. Measured: **roughly 780 of this
 * house's real members carry a `google_sub`**, which is not an edge case, it is nearly half the
 * membership. L10 paid for exactly this trap on the funnel's own pre-fill and the answer is the same
 * one: a `sessionStorage` stash, which survives a refresh AND the whole round trip to Google.
 *
 * **`sessionStorage` AND NOT `localStorage`, WHICH IS `onboarding.ts`'s OWN RECORDED REASON:** a new
 * session starts clean, so a landing intent cannot be poured over whoever uses that browser next
 * week. And the read is **THROW-SAFE**, because a storage that refuses is a browser setting rather
 * than a fact about her arrival, and a throw here is a white screen on the one page her advertising
 * pays for (R19: a crash is not a refusal).
 * ================================================================================================
 * >>> AND THE DEFAULT IS `my-room`, SO EVERY SIGNUP THAT IS NOT A TALK ARRIVAL IS BYTE-IDENTICAL.
 * <<<
 *
 * ITEM 12's neutrality argument, on the one flow every member on this site walks through. Her F4-3
 * clause lives inside the F4 block and is about the stranger who came through THIS door; a build
 * that sent every new member to Ask Lala instead of her room would be a member-visible change to the
 * whole signup flow she did not order (scope rule 4) **and it would break ONBOARD-RESTORE's own
 * rendered proof**, which measures a fresh email signup landing in HER ROOM at coordinates.
 * ================================================================================================
 */

/** The page id and the URL. One spelling each, so the router map and this law cannot disagree. */
export const TALK_PAGE = "talk";
export const TALK_PATH = "/talk";

/** Where a talk arrival lands once her room exists, and where everybody else lands. */
export const TALK_LANDING = "ask-lala";
export const DEFAULT_LANDING = "my-room";

/**
 * The stash key and its one value. The VALUE is checked rather than mere presence, because
 * `sessionStorage` is shared with every other stash this house writes and a truthy read of somebody
 * else's key is a member landing somewhere nobody sent her.
 */
export const TALK_STASH_KEY = "twelve_house_talk_landing";
export const TALK_STASH_VALUE = "ask-lala";

/**
 * >>> HER *ONE ACTION*, AS A LAW RATHER THAN A CONDITION AT A CALL SITE. <<<
 *
 * A member who is ALREADY SIGNED IN has a room and a chart, so sending her through signup is a door
 * that asks her to do something she has already done — and she would never reach `enterRoom()` at
 * all, so the stash would sit unread and the landing would silently never happen. She goes straight
 * to the conversation.
 *
 * >>> AND A STRANGER STAYS ON THIS PAGE, BECAUSE HER ITEM 2 NAMES NO SIGNUP STEP IN THIS FUNNEL AT
 * ALL. <<< Her own text, quotable from the OWNER PRIORITY LAW at the top of RUNBOOK.md, is *"the
 * /talk capture page … the form … the review carousel, the animated glowing generating state, then
 * LALA ARRIVES in the glowing plum window speaking FIRST BY NAME"* — capture, glow, arrival, and
 * nothing between them.
 *
 * This branch used to answer `signup`, and F4-1's reasoning for it was sound on the day it was
 * written: her chart had to exist before Lala spoke, and signing her up first was how that was
 * arranged. **ITEM 2's CAPTURE replaced that** — the capture takes her details ONCE, mints the
 * account behind the glow and waits for the cast — so the signup answer became a second, worse way
 * to do a thing this page already does, pointed at a step her ITEM 2 does not have.
 *
 * **IT WAS UNREACHABLE FROM THE RENDERED DOOR AND THAT IS EXACTLY WHY IT IS DELETED RATHER THAN
 * LEFT** (R52/R138): `talkShowsCapture` draws the door only for a member who IS signed in — measured
 * at the call site, `TalkPage` is handed `signedIn={!talkShowsCapture(...)}` and renders the CAPTURE
 * whenever that is false — so the signed-out arm could not fire. **This change is therefore
 * BEHAVIOUR-NEUTRAL for every member on this site** and what it removes is dead code stating the
 * opposite of her flow, one edit away from being live, on the page her advertising pays for. A
 * branch nothing can reach is the one a later round wires up by reflex (R165's shape).
 *
 * The stash is still written, because it costs nothing and is exactly right the day she does build
 * a room by another path: `landingAfterRoom` carries her to the conversation rather than the tour.
 */
export function talkStartPlan({ isLoggedIn = false } = {}) {
  if (isLoggedIn === true) return { go: TALK_LANDING, stash: false };
  return { go: TALK_PAGE, stash: true };
}

/**
 * >>> `TALK_SIGNUP_NEXT` IS DELETED AT ITS DECLARATION, BECAUSE THE PATH IT NAMED NO LONGER EXISTS.
 * <<<
 *
 * It answered *where a talk arrival lands AFTER signup*, and her ITEM 2 names no signup in this
 * funnel at all: the capture mints the account itself and the wait state carries her to the
 * window. A constant naming a step of a retired path is one a later round finds
 * and wires back up (DL-2/DT-2b/R181 — retire at the declaration, never leave the old value standing
 * beside its replacement).
 *
 * What its reasoning was really about SURVIVES and is stronger: **a brand-new member must never be
 * dropped into the conversation with no chart to read.** That is `chartPollVerdict`'s job now — the
 * capture holds the page until the cast is real — which is the same law enforced at the moment it
 * actually matters rather than by routing her through a form.
 */

/** Presence, never truthiness (R53/R136/R137): only the exact marker this door writes is a talk
 *  arrival, so another stash's value, a `true`, a `1` or an empty string all land in her room. */
export function landingAfterRoom(stashed) {
  return stashed === TALK_STASH_VALUE ? TALK_LANDING : DEFAULT_LANDING;
}

/** Write the landing intent. A storage that refuses is not an error worth losing the tap over. */
export function writeTalkLanding(store) {
  try {
    store?.setItem?.(TALK_STASH_KEY, TALK_STASH_VALUE);
  } catch {
    /* a browser with storage switched off still gets the door; she lands in her room. */
  }
}

/** Read it, and CLEAR it in the same act. It is consumed by a real tap on Enter rather than during a
 *  render, so StrictMode's double invocation cannot reach it (IT #146's ruling, one door over) — and
 *  a landing intent left standing would carry into a second room this member never builds. */
export function readTalkLanding(store) {
  try {
    const value = store?.getItem?.(TALK_STASH_KEY) ?? null;
    store?.removeItem?.(TALK_STASH_KEY);
    return typeof value === "string" ? value : null;
  } catch {
    return null;
  }
}

/** Cleared without reading, for a caller that wants the intent gone rather than acted on. */
export function clearTalkLanding(store) {
  try {
    store?.removeItem?.(TALK_STASH_KEY);
  } catch {
    /* nothing to do: an unreachable storage holds no intent to clear. */
  }
}

/**
 * >>> HER WORDS ARE THE COPY, AND THEY LIVE HERE RATHER THAN IN THE COMPONENT. <<<
 *
 * `FUNNEL_COPY` is this house's precedent one door over: an ad landing page's copy is a `.js` roster
 * a gate can RUN, English-only, and ITEM 14b is green over it — a stranger off an advertisement has
 * not chosen a language yet, and the funnel doors have never carried one.
 *
 * The hook is her sentence verbatim. **The action names what the tap GETS her rather than the
 * machinery** (S1-A/R117) — a stranger does not know what an account is for yet, and *Sign up* on
 * this page is the price before the desire, which is HOME-1's own ruling.
 */
export const TALK_COPY = Object.freeze({
  hook: "She already knows.",
  lede: "Ask her anything. Your chart is read before you say a word.",
  action: "Ask her anything",
});

/**
 * >>> **WHICH FACE `/talk` SHOWS, AND WHY IT CANNOT BE `signedIn` ALONE (2026-08-24).** <<<
 *
 * ITEM 2's rule is R18 and stands: **a stranger meets the CAPTURE and a member meets the DOOR** —
 * asking a member who already has a room to type her birth details again is the worst thing this
 * page could do to her.
 *
 * **AND HER CAPTURE CREATES THE ACCOUNT BEFORE IT CASTS THE CHART, WHICH TURNS THAT RULE ON ITSELF
 * MID-SUBMIT.** `TalkCapture` calls `onMember` the moment `/api/auth/login` answers — deliberately,
 * so the room create below it travels with a session the app already knows about — so `isLoggedIn`
 * flips to TRUE **while her chart is still casting**, the page swaps to the door, and **the glowing
 * state she is supposed to be watching is unmounted.** Measured at coordinates, BOTH viewports
 * (`f49 --spend`): the landing read `/talk ("She already knows.")` — the door, a minute after she
 * pressed her own button, with her form gone and nothing to say why.
 *
 * That is her report word for word: *"instead of the glowing text box pop-up."*
 *
 * **SO THE FACE IS TWO FACTS RATHER THAN ONE (R18): whether she is signed in, and whether a capture
 * on this page is IN FLIGHT.** A submit that is still running owns the page until it lands. The
 * default is `false`, so every caller that does not know about a capture is byte-identical (ITEM
 * 12's neutrality argument) and F4-1's own ruling is untouched for the member who merely arrives.
 */
export function talkShowsCapture({ signedIn = false, capturing = false } = {}) {
  if (capturing === true) return true;
  return signedIn !== true;
}
